/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import invert from 'lodash/invert';
import mapKeys from 'lodash/mapKeys';
// eslint-disable-next-line lodash/import-scope
import type { Dictionary as LodashDictionary } from 'lodash';
import type { OptionType, OptionsType } from '@atlaskit/select/types';
import type { Query } from '@atlassian/react-resource-router';

type Dictionary = { [key: string]: string };

export const matchAliases = <T extends Dictionary>(
	filterState: T,
	query: Query,
	aliases: { [alias: string]: keyof T },
): T =>
	Object.keys(query).reduce(
		(filter, key) => {
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const alias = aliases[key] as string;
			const queryValue = query[key];
			const hasInitialValue = filter[key] !== undefined;

			// override default filter value with query params
			if (hasInitialValue && queryValue) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, no-param-reassign
				(filter as Dictionary)[key] = queryValue;
				return filter;
			}

			// if there's no alias and no query param for it return default filter
			if (!alias || !queryValue) {
				return filter;
			}

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, no-param-reassign
			(filter as Dictionary)[alias] = queryValue;
			return filter;
		},
		{ ...filterState },
	);

/**
 * @param data - data that is gonna be serialized into string
 * @param aliases - should follow pattern {[alias]: stateKey}
 */
// todo
export const serializeToQueryString = (data: object, aliases: object): LodashDictionary<never> => {
	const invertedAliases = invert(aliases);
	return mapKeys(data, (_, key) => invertedAliases[key] || key);
};

export const serializeSelectOptions = (options: OptionsType<OptionType>): string =>
	options.map((v) => v.value).join(',');

export const serializeSelectOptionsLabel = (options: OptionsType<OptionType>): string =>
	options.map((v) => v.label).join(',');

export const matchValueToSelectOptions = (
	value: string | undefined,
	options: OptionsType<OptionType>,
): OptionsType<OptionType> =>
	value
		? // oxlint-disable-next-line typescript/consistent-type-assertions
			(value
				.split(',')
				.map((val) => options.find((option) => option.value === val))
				.filter(Boolean) as OptionsType<OptionType>)
		: [];
